import React from "react";
import { Alert, Card, CardBody, Col, Row, Table } from "reactstrap";
import { formatearFacturaSegmento } from "../helpers";
import { CajaDiaAnterior, FacturaAnulada, ResumenEstado, SucursalRendicion } from "../types";

interface ConfirmacionStepProps {
  sucursalSeleccionada: SucursalRendicion | null;
  cajaSeleccionada: CajaDiaAnterior | null;
  totalEfectivo: number;
  totalGastos: number;
  totalFormasPago: number;
  totalBoletas: number;
  rangoFacturasTexto: string;
  facturasAnuladas: FacturaAnulada[];
  notaFondos: string;
  resumen: ResumenEstado | null;
  vistaPreviaHtml?: string | null;
  tiempoRestanteConfirmacion?: number | null;
}

const ConfirmacionStep = ({
  sucursalSeleccionada,
  cajaSeleccionada,
  totalEfectivo,
  totalGastos,
  totalFormasPago,
  totalBoletas,
  rangoFacturasTexto,
  facturasAnuladas,
  notaFondos,
  resumen,
  vistaPreviaHtml,
  tiempoRestanteConfirmacion,
}: ConfirmacionStepProps) => {
  const formatoTiempo = (valor?: number | null) => {
    if (valor == null || valor < 0) {
      return null;
    }
    const minutos = Math.floor(valor / 60);
    const segundos = valor % 60;
    return `${String(minutos).padStart(2, "0")}:${String(segundos).padStart(2, "0")}`;
  };
  const tiempoTexto = formatoTiempo(tiempoRestanteConfirmacion);
  return (
  <Row className="g-4">
    <Col lg={6}>
      <Card>
        <CardBody>
          <h5 className="mb-3">Resumen final</h5>
          {!sucursalSeleccionada || !cajaSeleccionada ? (
            <Alert color="warning">Elegí una sucursal y caja para continuar.</Alert>
          ) : (
            <div className="table-responsive">
              <Table striped>
                <tbody>
                  <tr>
                    <th style={{ width: "40%" }}>Sucursal</th>
                    <td>{sucursalSeleccionada.sucursalNombre ?? cajaSeleccionada.sucursal ?? "-"}</td>
                  </tr>
                  <tr>
                    <th>Caja</th>
                    <td>Caja {cajaSeleccionada.idcontrolcaja}</td>
                  </tr>
                  <tr>
                    <th>Total efectivo</th>
                    <td>Gs. {totalEfectivo.toLocaleString("es-PY")}</td>
                  </tr>
                  <tr>
                    <th>Total gastos</th>
                    <td>Gs. {totalGastos.toLocaleString("es-PY")}</td>
                  </tr>
                  <tr>
                    <th>Total formas de pago</th>
                    <td>Gs. {totalFormasPago.toLocaleString("es-PY")}</td>
                  </tr>
                  <tr>
                    <th>Total boletas</th>
                    <td>Gs. {totalBoletas.toLocaleString("es-PY")}</td>
                  </tr>
                  <tr>
                    <th>Rango de facturas</th>
                    <td>{rangoFacturasTexto}</td>
                  </tr>
                  <tr>
                    <th>Facturas anuladas</th>
                    <td>
                      {facturasAnuladas.length === 0 ? (
                        <span className="text-muted">Sin registros</span>
                      ) : (
                        <ul className="mb-0 ps-3">
                          {facturasAnuladas.map((factura) => (
                            <li key={factura.id}>
                              {formatearFacturaSegmento(factura) ?? "Factura sin número"}
                              {factura.motivo ? ` · ${factura.motivo}` : ""}
                            </li>
                          ))}
                        </ul>
                      )}
                    </td>
                  </tr>
                  <tr>
                    <th>Observaciones</th>
                    <td>{notaFondos || "Sin comentarios"}</td>
                  </tr>
                </tbody>
              </Table>
            </div>
          )}
          {resumen && (
            <Alert color={resumen.tipo === "danger" ? "danger" : "success"} className="mt-3">
              {resumen.mensaje}
              {resumen.fecha && <div className="fw-semibold">Confirmado el {resumen.fecha}</div>}
            </Alert>
          )}
        </CardBody>
      </Card>
    </Col>
    <Col lg={6}>
      <Card>
        <CardBody>
          <div className="d-flex justify-content-between align-items-start">
            <div>
              <h5 className="mb-1">Vista previa</h5>
              <p className="mb-0 text-muted">
                Descargá o imprimí el comprobante para adjuntar a tu rendición junto con los documentos físicos.
              </p>
              {typeof tiempoRestanteConfirmacion === "number" && tiempoRestanteConfirmacion > 0 && (
                <Alert color="danger" className="py-2 mt-2 mb-0">
                  Tenés <strong>{tiempoTexto}</strong> para confirmar antes de reiniciar la rendición.
                </Alert>
              )}
              {tiempoRestanteConfirmacion === 0 && (
                <Alert color="danger" className="py-2 mt-2 mb-0">
                  El tiempo expiró. Debés reiniciar la rendición para confirmar nuevamente.
                </Alert>
              )}
            </div>
            {resumen?.fecha && (
              <span className="badge bg-success">Confirmada</span>
            )}
          </div>
          <div className="border rounded bg-white overflow-hidden mt-3" style={{ minHeight: 360 }}>
            {vistaPreviaHtml ? (
              <iframe
                title="Vista previa de la rendición"
                srcDoc={vistaPreviaHtml}
                style={{ width: "100%", border: 0, minHeight: 360 }}
              />
            ) : (
              <div className="h-100 d-flex align-items-center justify-content-center text-muted">
                Generando vista previa…
              </div>
            )}
          </div>
        </CardBody>
      </Card>
    </Col>
  </Row>
  );
};

export default ConfirmacionStep;
